import React, { createContext, useCallback, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';

import { Contact, Device, Loadable } from '../types';
import { EmergencyContact } from '../types/EmergencyContact';
import { contactService } from '../api/services/contactService';

interface ContactContextType {
  device?: Device;
  contacts: Loadable<Contact>;
  emergencyContacts: Loadable<EmergencyContact>;
  refreshContacts: () => Promise<void>;
  saveContact: (contact: Contact, invite: boolean, reInvite: boolean) => Promise<Contact>;
  removeContact: (contactId: string) => Promise<void>;
  saveEmergencyContact: (emergencyContact: EmergencyContact) => Promise<EmergencyContact>;
  removeEmergencyContact: (contactId: string) => Promise<void>;
}

const loadableContacts: Loadable<Contact> = { loading: true };
const loadableEmergencyContacts: Loadable<EmergencyContact> = { loading: true };

const initialContext: ContactContextType = {
  contacts: loadableContacts,
  emergencyContacts: loadableEmergencyContacts,
  refreshContacts: () => Promise.resolve(),
  saveContact: () => Promise.resolve({} as Contact),
  removeContact: () => Promise.resolve(),
  saveEmergencyContact: () => Promise.resolve({} as EmergencyContact),
  removeEmergencyContact: () => Promise.resolve(),
};

const ContactContext = createContext<ContactContextType>(initialContext);

interface ContactProviderProps {
  device?: Device;
  children: React.ReactNode;
}

function ContactProvider({ device, children }: ContactProviderProps) {
  const [contacts, setContacts] = useState<Loadable<Contact>>({ loading: true });
  const [emergencyContacts, setEmergencyContacts] = useState<Loadable<EmergencyContact>>({
    loading: true,
  });

  // Function to refresh contacts - memoized to prevent recreation on every render
  const refreshContacts = useCallback(async (): Promise<void> => {
    if (!device) return Promise.resolve();

    setContacts({ loading: true });
    try {
      const response = await contactService.getContacts(device.id);
      const data = response.data.map((a: Contact) => ({ ...a, createdAt: new Date(a.createdAt) }));
      setContacts({ loading: false, data });
    } catch (err) {
      setContacts({ loading: false, error: err as Error });
    }
    return Promise.resolve();
  }, [device]);

  useEffect(() => {
    if (!device) return;

    let isMounted = true;

    // Load contacts
    refreshContacts();

    // Load emergency contacts
    setEmergencyContacts({ loading: true });
    contactService
      .getEmergencyContacts(device.id)
      .then((response) =>
        response.data.map((a: EmergencyContact) => ({ ...a, createdAt: new Date(a.createdAt) })),
      )
      .then((data) => {
        if (isMounted) setEmergencyContacts({ loading: false, data });
      })
      .catch((err) => {
        if (isMounted) setEmergencyContacts({ loading: false, error: err });
      });

    return () => {
      isMounted = false;
    };
  }, [device, refreshContacts]);

  const saveContact = (
    contact: Contact,
    invite: boolean = false,
    reInvite: boolean = false,
  ): Promise<Contact> => {
    if (!device) return Promise.reject('no device');

    const handleResponse = (
      resolve: Function,
      response: AxiosResponse<Contact>,
      isNew: boolean,
    ) => {
      const existingData = Array.isArray(contacts.data) ? contacts.data : [];
      const data = isNew
        ? [...existingData, response.data]
        : existingData.map((c) => (c.id === contact.id ? response.data : c));
      setContacts({ loading: false, data });
      resolve(response.data);
    };

    const handleError = (reject: Function, err: unknown) => {
      setContacts({ loading: false, error: err as Error });
      reject(err);
    };

    return new Promise((resolve, reject) => {
      const request = invite
        ? contactService.inviteContact(device.id, contact)
        : contact.id
          ? contactService.updateContact(device.id, contact.id, contact)
          : contactService.createContact(device.id, contact);

      request
        .then((response) => handleResponse(resolve, response, !contact.id || (invite && !reInvite)))
        .catch((err) => handleError(reject, err));
    });
  };

  const removeContact = async (contactId: string) => {
    if (!device) return Promise.reject('no device');
    try {
      await contactService.deleteContact(device.id, contactId);
      return setContacts({
        loading: false,
        data: contacts.data?.filter((c) => c.id !== contactId),
      });
    } catch (err) {
      return setContacts({ loading: false, error: err as Error });
    }
  };

  const saveEmergencyContact = (emergencyContact: EmergencyContact): Promise<EmergencyContact> => {
    if (!device) return Promise.reject('no device');

    const handleResponse = (
      resolve: Function,
      response: AxiosResponse<EmergencyContact>,
      isNew: boolean,
    ) => {
      const existingData = Array.isArray(emergencyContacts.data) ? emergencyContacts.data : [];
      const data = isNew
        ? [...existingData, response.data]
        : existingData.map((c) => (c.id === emergencyContact.id ? response.data : c));
      setEmergencyContacts({ loading: false, data });
      resolve(response.data);
    };

    const handleError = (reject: Function, err: unknown) => {
      setEmergencyContacts({ loading: false, error: err as Error });
      reject(err);
    };

    return new Promise((resolve, reject) => {
      const request = emergencyContact.id
        ? contactService.updateEmergencyContact(device.id, emergencyContact.id, emergencyContact)
        : contactService.createEmergencyContact(device.id, emergencyContact);

      request
        .then((response) => handleResponse(resolve, response, !emergencyContact.id))
        .catch((err) => handleError(reject, err));
    });
  };

  const removeEmergencyContact = async (emergencyContactId: string) => {
    if (!device) return Promise.reject('no device');
    try {
      await contactService.deleteEmergencyContact(device.id, emergencyContactId);
      return setEmergencyContacts({
        loading: false,
        data: emergencyContacts.data?.filter((ec) => ec.id !== emergencyContactId),
      });
    } catch (err) {
      return setEmergencyContacts({ loading: false, error: err as Error });
    }
  };

  return (
    <ContactContext.Provider
      value={{
        device,
        contacts,
        emergencyContacts,
        refreshContacts,
        saveContact,
        removeContact,
        saveEmergencyContact,
        removeEmergencyContact,
      }}
    >
      {children}
    </ContactContext.Provider>
  );
}

export { ContactContext, ContactProvider };
